import type { PropsWithChildren, ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

interface PageContainerProps extends PropsWithChildren {
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    subtitleSx?: SxProps<Theme>;
    action?: ReactNode;
    /** Action до заглавието (напр. Експорт), не вдясно в края на реда. */
    actionBesideTitle?: boolean;
    middleText?: string;
}

export default function PageContainer({
    eyebrow,
    title,
    subtitle,
    subtitleSx,
    action,
    actionBesideTitle = false,
    children,
}: PageContainerProps) {
    const titleBlock = (
        <>
            {eyebrow && (
                <Typography
                    variant="overline"
                    sx={{ letterSpacing: 1.6, color: "primary.main", fontWeight: 800 }}
                >
                    {eyebrow}
                </Typography>
            )}

            {title && (
                <Typography
                    variant="h3"
                    sx={{
                        mt: eyebrow ? 0.5 : 0,
                        fontSize: { xs: "1.35rem", sm: "1.6rem", md: undefined },
                        lineHeight: { xs: 1.2, md: 1.15 },
                    }}
                >
                    {title}
                </Typography>
            )}
        </>
    );

    return (
        <Box sx={{ display: "grid", gap: { xs: 1, sm: 1.5 }, width: "100%", minWidth: 0, maxWidth: "100%" }}>
            {(eyebrow || title || subtitle || action) && (
                actionBesideTitle ? (
                    <Stack spacing={subtitle ? 1 : 0} sx={{ minWidth: 0 }}>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                                alignItems: "center",
                                flexWrap: "wrap",
                                columnGap: 1.5,
                                rowGap: 1,
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>{titleBlock}</Box>
                            {action ? (
                                <Box sx={{ flexShrink: 0, "& .MuiButton-root": { maxWidth: "100%" } }}>
                                    {action}
                                </Box>
                            ) : null}
                        </Stack>
                        {subtitle ? (
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ maxWidth: { xs: "100%", lg: 820 }, ...subtitleSx }}
                            >
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Stack>
                ) : (
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        sx={{
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", md: "flex-end" },
                        }}
                    >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            {titleBlock}

                            {subtitle && (
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ mt: 1, maxWidth: { xs: "100%", lg: 820 }, ...subtitleSx }}
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>

                        {action && (
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: { xs: "100%", md: 560, lg: 640 },
                                    minWidth: 0,
                                    flexShrink: 1,
                                    "& .MuiButton-root": {
                                        maxWidth: "100%",
                                    },
                                }}
                            >
                                {action}
                            </Box>
                        )}
                    </Stack>
                )
            )}

            {children}
        </Box>
    );
}
